import { DOCUMENT } from '@angular/common';
import {
  EnvironmentProviders,
  Provider,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { RouterStateSerializer } from '@ngrx/router-store';
import { provideOktaAuth, withOktaConfig } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

import { HTTP_INTERCEPTORS } from '@angular/common/http';

import type { GtagFn } from 'ngx-google-analytics';
import {
  GoogleAnalyticsInitializer,
  NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN,
  NGX_GTAG_FN,
} from 'ngx-google-analytics';
import { environment } from 'src/environments/environment';
import config from './app.config';
import { NonceService } from './core/services/nonce';
import { TokenInterceptor } from './core/token.interceptor';
import { CustomSerializer } from './store/reducers/router.reducer';

export const googleMeasurementId = environment.production
  ? 'G-S1MYCEH0GM'
  : 'G-8V2BK3ZR53';

export function googleAnalyticsInitializerFactory(
  nonceService: NonceService,
  gtag: GtagFn,
  document: Document,
): () => Promise<void> {
  return async () => {
    const nonce = await nonceService.getNonce();
    await GoogleAnalyticsInitializer(
      {
        trackingCode: googleMeasurementId,
        nonce,
        enableTracing: !environment.production,
      },
      gtag,
      document,
    )();
  };
}

const oktaAuth = new OktaAuth(config.oidc);

export const initializeAppProviders: (Provider | EnvironmentProviders)[] = [
  { provide: RouterStateSerializer, useClass: CustomSerializer },
  provideOktaAuth(withOktaConfig({ oktaAuth })),
  { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
  {
    provide: NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN,
    useValue: {
      trackingCode: googleMeasurementId,
      enableTracing: !environment.production,
    },
  },
  provideAppInitializer(() =>
    googleAnalyticsInitializerFactory(
      inject(NonceService),
      inject(NGX_GTAG_FN),
      inject(DOCUMENT),
    )(),
  ),
];
